import Head from "next/head"
import { useRouter } from "next/router"

const checkCanonical = (canonical: string): string => {
  if (!canonical.startsWith("https://") || canonical.endsWith("/")) {
    throw new Error(`Invalid canonical url (${canonical})"`)
  }
  return canonical
}
export interface Metadata {
  title?: string
  description?: null | string
  canonical?: null | string
  image?: null | string
  url?: null | string
  noIndex?: boolean
}

interface Props {
  meta?: Metadata
  noIndex?: boolean
}

const formatMetaTitle = (title: string) => {
  if (title.trim().endsWith("| Token Terminal")) {
    // throw to ensure there's no misuse. Consider eg. if we wanna update the postfix at some point
    throw new Error(
      "Meta title postfixes are automatically set for consistency. Please dont add a manual one unless its a prefix.",
    )
  }
  // Allow prefix for front-pages
  if (title.includes("Token Terminal |")) {
    return title
  }
  return `${title} | Token Terminal`
}

const DOMAIN = "https://tokenterminal.com"

const selfCanonical = (asPath: string) => {
  const [plainPath] = asPath.split("?")
  // Remove trailing slash should only be necessary on frontpage
  const baseUrl = plainPath.startsWith("/resources") ? DOMAIN : `${DOMAIN}/resources`
  return `${baseUrl}${plainPath}`.replace(/\/$/, "")
}

/** App-wide self-canonical on tokenterminal.com, so Google indexes that copy and
 * not the landing.tokenterminal.dev origin. A page's own PageMeta overrides it. */
export const DefaultCanonical = () => {
  const { asPath, pathname, isFallback } = useRouter()
  if (isFallback || pathname === "/404" || pathname === "/_error") return null

  const [plainPath] = asPath.split(/[?#]/)
  return (
    <Head>
      <link key="canonical" rel="canonical" href={`${DOMAIN}${plainPath}`.replace(/\/$/, "")} />
    </Head>
  )
}

const getOptimizedImageUrl = (image: string): string => {
  // Strip own domain so /_next/image treats local images as local paths
  const path = image.startsWith(`${DOMAIN}/`) ? image.slice(DOMAIN.length) : image
  return `${DOMAIN}/_next/image?url=${encodeURIComponent(path)}&w=1200&q=75`
}

const OptimizedMetaImage = ({ image }: { image: string }): JSX.Element => {
  const src = getOptimizedImageUrl(image)
  return (
    <Head>
      <meta key="og:image" property="og:image" content={src} />
      <meta key="twitter:image" name="twitter:image" content={src} />
    </Head>
  )
}

// NOTE: keys are set to override _app.tsx values for non native meta props. Apparently it's needed (at least in dev)
export const PageMeta = ({
  meta: { title, description, canonical: forcedCanonical, url, image, noIndex: metaNoIndex } = {},
  noIndex,
}: Props) => {
  const { asPath } = useRouter()
  // Use passed canonical or create one. This is a SEO request/feature
  const canonical = forcedCanonical ?? selfCanonical(asPath)

  const shareUrl = url ?? canonical
  const formattedTitle = title && formatMetaTitle(title)

  return (
    <>
      <Head>
        {formattedTitle && (
          <>
            <title>{formattedTitle}</title>
            <meta property="og:title" key="og:title" content={formattedTitle} />
            <meta name="twitter:title" key="twitter:title" content={formattedTitle} />
          </>
        )}
        {description && (
          <>
            <meta name="description" content={description} />
            <meta property="og:description" key="og:description" content={description} />
            <meta name="twitter:description" key="twitter:description" content={description} />
          </>
        )}
        {shareUrl && <meta key="og:url" property="og:url" content={shareUrl} />}
        {canonical && <link key="canonical" rel="canonical" href={checkCanonical(canonical)} />}

        {(noIndex || metaNoIndex) && <meta key="robots" name="robots" content="noindex" />}
      </Head>
      {image && <OptimizedMetaImage image={image} />}
    </>
  )
}

PageMeta.displayName = "PageMeta"
